import { BorderStyle, Document, HeadingLevel, Packer, Paragraph, TextRun } from "docx";
import type { TElement, TText } from "platejs";
import type { AssemblyProject } from "../../../common/contexts/SelectionContext";
import {
  BLOCK_CHECKLIST,
  BLOCK_LOG,
  BLOCK_NOTA,
  BLOCK_OTF,
  BLOCK_PLECA,
  BLOCK_TRANSCRIPT,
  type ChecklistBlockElement,
} from "./schema";
import { formatDateLabel, pickDateSource } from "./blocks/dateLabel";
import { sanitizeFilename, type StreamTagResolver } from "./exportTxt";

function nodeText(node: TElement): string {
  return ((node.children ?? []) as TText[])
    .map((c) => (typeof c?.text === "string" ? c.text : ""))
    .join("")
    .trim();
}

// Mirror del helper en exportTxt/exportPdf — los tres exports usan la misma
// señal de origen para los OTF promovidos por OTF MODE.
function otfLabelWithOrigin(originType: unknown): string {
  if (originType === "log") return "OTF←LOG";
  if (originType === "transcript" || originType === "transcription") return "OTF←TRANS";
  return "OTF";
}

function headerLine(label: string | null, el: any): string {
  const date = formatDateLabel(pickDateSource(el));
  const speaker = el.speakerName ? String(el.speakerName) : "";
  // LOG/TRANSCRIPT pasan label=null para que su cabecera quede igual y los
  // bloques contiguos colapsen. OTF sí muestra label porque marca el origen.
  const labelPart = label ? `[${label}]` : "";
  const right = [labelPart, speaker].filter(Boolean).join(" ");
  if (!right) return date;
  return date ? `${date} — ${right}` : right;
}

function tagsLine(node: any, resolver?: StreamTagResolver): string {
  if (!resolver) return "";
  const labels = resolver(node);
  return labels.length ? `· ${labels.join(" · ")}` : "";
}

function isGroupableType(t: unknown): boolean {
  return t === BLOCK_LOG || t === BLOCK_OTF || t === BLOCK_TRANSCRIPT;
}

function groupKey(el: any): string {
  const date = formatDateLabel(pickDateSource(el));
  const speaker = el.speakerName ? String(el.speakerName) : "";
  if (el.type === BLOCK_OTF) {
    const origin = el.originType ? String(el.originType) : "";
    return `otf|${date}|${speaker}|${origin}`;
  }
  return `body|${date}|${speaker}`;
}

// Línea divisoria gris claro entre grupos — paragraph vacío con border bottom.
function dividerParagraph(): Paragraph {
  return new Paragraph({
    text: "",
    border: {
      bottom: {
        color: "C8C8C8",
        space: 1,
        style: BorderStyle.SINGLE,
        size: 6,
      },
    },
    spacing: { before: 120, after: 120 },
  });
}

function bodyParagraph(tc: string, text: string): Paragraph {
  const tcTrim = tc.trim();
  const runs: TextRun[] = [];
  if (tcTrim) {
    runs.push(new TextRun({ text: tcTrim, bold: true }));
    // Doble espacio entre TC y cuerpo para que no queden pegados.
    runs.push(new TextRun({ text: "  " }));
  }
  if (text) runs.push(new TextRun({ text }));
  if (runs.length === 0) runs.push(new TextRun({ text: "" }));
  return new Paragraph({ children: runs });
}

function blockParagraphs(
  node: TElement,
  resolver?: StreamTagResolver,
  groupedWithPrev = false,
): Paragraph[] {
  const text = nodeText(node);
  const tags = tagsLine(node, resolver);
  const tagsParagraphs = tags
    ? [new Paragraph({ children: [new TextRun({ text: tags, italics: true })] })]
    : [];

  const renderBody = (label: string | null): Paragraph[] => {
    const headParagraphs: Paragraph[] = groupedWithPrev
      ? []
      : [
          new Paragraph({
            children: [new TextRun({ text: headerLine(label, node), bold: true })],
            spacing: { before: 120, after: 40 },
          }),
        ];
    const tc = (node as any).tc ? String((node as any).tc) : "";
    return [...headParagraphs, ...tagsParagraphs, bodyParagraph(tc, text)];
  };

  switch (node.type) {
    case BLOCK_LOG:
    case BLOCK_TRANSCRIPT:
      return renderBody(null);
    case BLOCK_OTF:
      return renderBody(otfLabelWithOrigin((node as any).originType));
    case BLOCK_PLECA:
      return [
        new Paragraph({
          children: [new TextRun({ text: "PLECA: ", bold: true }), new TextRun({ text })],
        }),
      ];
    case BLOCK_NOTA:
      if (!text) return [];
      return [
        new Paragraph({
          children: [new TextRun({ text: "RECURSO: ", bold: true }), new TextRun({ text })],
        }),
      ];
    case BLOCK_CHECKLIST: {
      const c = node as ChecklistBlockElement;
      const head = new Paragraph({
        children: [new TextRun({ text: "[CHECKLIST DE CIERRE]", bold: true })],
      });
      const items = c.items.map(
        (it) =>
          new Paragraph({
            text: `[${it.done ? "x" : " "}] ${it.label}`,
            bullet: { level: 0 },
          }),
      );
      return [head, ...items];
    }
    case "h2":
      return text
        ? [new Paragraph({ text: text.toUpperCase(), heading: HeadingLevel.HEADING_2 })]
        : [];
    case "p":
    default:
      return text ? [new Paragraph({ text })] : [];
  }
}

export function buildExportDoc(
  project: AssemblyProject,
  titulo: string,
  sinopsis: string,
  doc: TElement[],
  autor: string,
  resolveStreamTags?: StreamTagResolver,
): Document {
  const fecha = project.createdAt ? new Date(project.createdAt) : new Date();
  const fechaStr = fecha.toLocaleDateString("es-MX", {
    day: "2-digit",
    month: "long",
  });

  const children: Paragraph[] = [
    // Título: bold 20pt (size en half-points = 40).
    new Paragraph({
      children: [new TextRun({ text: titulo || "Sin título", bold: true, size: 40 })],
      spacing: { after: 120 },
    }),
    new Paragraph({
      children: [new TextRun({ text: fechaStr, italics: true })],
    }),
    new Paragraph({
      children: [new TextRun({ text: autor || "—", bold: true })],
    }),
  ];
  if (sinopsis?.trim()) {
    children.push(
      new Paragraph({
        children: [new TextRun({ text: sinopsis.trim(), bold: true })],
        spacing: { before: 120 },
      }),
    );
  }
  children.push(dividerParagraph());

  // Bloques. Recorrido con `lastEmitted` (no el bloque crudo previo): un bloque
  // descartado en el medio — nota vacía, p.ej. — no debe romper el grupo.
  let lastEmitted: TElement | null = null;
  let firstBlock = true;
  for (const n of doc) {
    const grouped =
      !!lastEmitted &&
      isGroupableType(n.type) &&
      isGroupableType(lastEmitted.type) &&
      groupKey(n) === groupKey(lastEmitted);
    const paragraphs = blockParagraphs(n, resolveStreamTags, grouped);
    if (paragraphs.length === 0) continue;
    if (!firstBlock && !grouped) children.push(dividerParagraph());
    children.push(...paragraphs);
    lastEmitted = n;
    firstBlock = false;
  }

  return new Document({
    styles: {
      default: {
        document: {
          run: { font: "Arial", size: 22 }, // 11pt
        },
      },
    },
    sections: [{ children }],
  });
}

export async function downloadDoc(filename: string, docFile: Document): Promise<void> {
  const blob = await Packer.toBlob(docFile);
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 0);
}

export { sanitizeFilename };
